import * as z from "zod";

/**
 * Access scopes for the GraphQL public API tokens stored in CockroachDB
 * (`public_api_auth_token.scopes`).
 *
 * This is the canonical list for the application layer (merchant RPC + admin
 * UI). It MUST stay in sync with two other definitions:
 *   - The CRDB enum `public_api_auth_scope`
 *     (redo/cockroachdb/schema/definitions/tables/common/public-api-auth-token.sql)
 *   - The lean local copy in the GraphQL server's auth module
 *     (redo/graphql-public/server/src/auth.ts), which is kept separate so that
 *     package does not have to depend on @redotech/redo-model.
 *
 * The CRDB enum is the ultimate backstop: inserting an unknown value fails.
 */
export const PUBLIC_API_AUTH_SCOPES = [
  "orders_read",
  "orders_write",
  "customers_read",
  "customers_write",
  "products_read",
  "products_write",
  "returns_read",
  "returns_write",
  "webhooks_read",
  "webhooks_write",
  "inventory_read",
  "inbound_shipments_read",
  "invoices_read",
  "customer_events_write",
  "storefront_events_write",
] as const;

export type PublicApiAuthScope = (typeof PUBLIC_API_AUTH_SCOPES)[number];

export const publicApiAuthScopeSchema = z.enum(PUBLIC_API_AUTH_SCOPES);

/**
 * Prefix on every generated public API token. Vendor-namespaced so secret
 * scanners can detect leaks, and signals that the token is a secret credential.
 * The prefix is part of the token string that gets SHA-256 hashed, so changing
 * it would invalidate all previously-issued tokens.
 */
export const PUBLIC_API_TOKEN_PREFIX = "redo_sk_";

export type PublicApiScopeAccess = "read" | "write";

export interface PublicApiScopeInfo {
  scope: PublicApiAuthScope;
  /** Grouping key shared by a resource's read/write scopes, e.g. "orders". */
  resource: string;
  /** Human-readable resource name for the scope picker, e.g. "Orders". */
  resourceLabel: string;
  access: PublicApiScopeAccess;
}

export const PUBLIC_API_SCOPE_INFO: Record<
  PublicApiAuthScope,
  PublicApiScopeInfo
> = {
  orders_read: {
    scope: "orders_read",
    resource: "orders",
    resourceLabel: "Orders",
    access: "read",
  },
  orders_write: {
    scope: "orders_write",
    resource: "orders",
    resourceLabel: "Orders",
    access: "write",
  },
  customers_read: {
    scope: "customers_read",
    resource: "customers",
    resourceLabel: "Customers",
    access: "read",
  },
  customers_write: {
    scope: "customers_write",
    resource: "customers",
    resourceLabel: "Customers",
    access: "write",
  },
  products_read: {
    scope: "products_read",
    resource: "products",
    resourceLabel: "Products",
    access: "read",
  },
  products_write: {
    scope: "products_write",
    resource: "products",
    resourceLabel: "Products",
    access: "write",
  },
  returns_read: {
    scope: "returns_read",
    resource: "returns",
    resourceLabel: "Returns",
    access: "read",
  },
  returns_write: {
    scope: "returns_write",
    resource: "returns",
    resourceLabel: "Returns",
    access: "write",
  },
  webhooks_read: {
    scope: "webhooks_read",
    resource: "webhooks",
    resourceLabel: "Webhooks",
    access: "read",
  },
  webhooks_write: {
    scope: "webhooks_write",
    resource: "webhooks",
    resourceLabel: "Webhooks",
    access: "write",
  },
  inventory_read: {
    scope: "inventory_read",
    resource: "inventory",
    resourceLabel: "Inventory",
    access: "read",
  },
  inbound_shipments_read: {
    scope: "inbound_shipments_read",
    resource: "inbound_shipments",
    resourceLabel: "Inbound shipments",
    access: "read",
  },
  invoices_read: {
    scope: "invoices_read",
    resource: "invoices",
    resourceLabel: "Invoices",
    access: "read",
  },
  customer_events_write: {
    scope: "customer_events_write",
    resource: "customer_events",
    resourceLabel: "Customer events",
    access: "write",
  },
  storefront_events_write: {
    scope: "storefront_events_write",
    resource: "storefront_events",
    resourceLabel: "Storefront events",
    access: "write",
  },
};
